// Storage for external client accounts (DESIGN rev 2.1 §3.3 Portal, HD-9). Staff keep the Console page (pages/Storage.jsx).
// Light by default with the toggle (the tier comes from the shell, see AppShell's PortalRail), low density: folders, then
// files, as Portal List rows. No Grid, no Expanded-row panel, no Command palette.
//
// What a client can do here, each with the call the Console page made for them (same params, same bodies):
//   browse          GET BASE/summary, GET BASE/folders?clientId, GET BASE/folders/:id|root/children,
//                   GET BASE/files (clientId, page, pageSize 50, folderId all|<id>, search)
//   deep link       ?fileId=<id>: GET BASE/files/:id, then that file's folder (the file is shown first, with its Download)
//   download        components/StorageDownloadModal (its own calls: GET BASE/files/:id/download, or the folder transfer)
//   upload files    the multipart routes + POST BASE/upload/confirm, into a folder or Unfiled (the client allowlist)
//   upload folder   POST BASE/folders/resolve (dry run, then real), then the files as above
//   new folder      POST BASE/folders { clientId, parentId, name }
//   own uploads     (HD-10, Owner ruling) a row menu beside Download, on files the client uploaded (mayDeleteFile's test):
//                   Rename PATCH BASE/files/:id { fileName }, Move { folderId } (this client's folders or Unfiled),
//                   Notes { notes }: the Console file panel's bodies; Delete DELETE BASE/files/:id
// Not here (staff-only, or never allowed to a client): any edit on a file someone else uploaded, a move to another
// client, promote, folder rename / move / delete, deleted files, bulk actions, the client switcher, the gallery view.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowUp, ChevronRight, Download, File, FileAudio, FileImage, FileText, FileVideo, Folder, FolderInput, FolderPlus, FolderUp,
  MoreHorizontal, Pencil, Search, StickyNote, Trash2, Upload, X,
} from 'lucide-react';
import api from '../../lib/api';
import {
  entriesFromPicker, entriesFromDataTransfer, dropContainsDirectory, buildPreflight, formatBytes,
} from '../../lib/folderUpload';
import { useAuth } from '../../context/AuthContext';
import StorageDownloadModal, { StorageDownloadDock } from '../../components/StorageDownloadModal';
import { dismissFinishedDownloads } from '../../lib/storageDownload';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import EmptyState from '../../ui/EmptyState';
import ErrorState from '../../ui/ErrorState';
import Icon from '../../ui/Icon';
import Input from '../../ui/Input';
import ListRow from '../../ui/ListRow';
import Menu from '../../ui/Menu';
import Modal from '../../ui/Modal';
import Popover from '../../ui/Popover';
import Select from '../../ui/Select';
import Skeleton from '../../ui/Skeleton';
import Textarea from '../../ui/Textarea';
import { useConfirm } from '../../ui/Confirm';
import { useToast } from '../../ui/Toast';
import { useMedia, usePortalRailTier } from '../../shell/AppShell';
import { usePageHeader } from '../../shell/pageHeader';
import { PortalTierToggle } from '../../shell/portalTier';
import { clean } from '../../shell/copy';
import { enqueueUpload, useUploadDone } from '../../shell/uploads';
import {
  ACCEPT_EXTS, ACCEPT_RE, BASE, PAGE_SIZE, UPLOAD_ENDPOINTS, deleteBody, disambiguate, errMsg, flattenTree, fmtDate, indentOptions,
  mayDeleteFile, plural,
} from './archive';
import PreflightModal from './PreflightModal';

const KIND_ICON = [
  [/\.(mp4|mov|avi|mkv|m4v|mxf|r3d|braw)$/i, FileVideo],
  [/\.(wav|aif|aiff|mp3|aac|m4a|flac|ogg)$/i, FileAudio],
  [/\.(jpe?g|png|webp|gif|bmp|heic|heif|avif|svg|tiff?|psd|ai|eps)$/i, FileImage],
  [/\.(pdf|docx?|txt|rtf|csv|xlsx|md)$/i, FileText],
];
const kindIcon = name => (KIND_ICON.find(([re]) => re.test(name || '')) || [null, File])[1];

const PREFLIGHT_NOTE = 'The folder structure is recreated here, and each file is kept in your archive.';

export default function PortalStorage() {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const tier = usePortalRailTier();
  const phone = useMedia('(max-width: 600px)');

  // An external account writes when it is joined to a client (the API's canWrite for external callers).
  const canWrite = !!user?.assignedClientIds?.length;
  const deletePerm = useMemo(() => ({ isExternal: true, isAdmin: false, userId: user?.id || null }), [user?.id]);

  // Where you are lives in the URL, as on the Console page: clientId, folderId ('all' | 'unfiled' | <folder id>), and
  // ?fileId= consumed on arrival. A move the user makes pushes a history entry; a move the page makes replaces it.
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') || '';
  const rawFolder = searchParams.get('folderId') || 'all';
  const selected = rawFolder === 'root' ? 'unfiled' : rawFolder;

  const go = useCallback((next, { replace = false } = {}) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('fileId');
      if ('client' in next) { if (next.client) p.set('clientId', next.client); else p.delete('clientId'); }
      if ('folder' in next) { if (next.folder) p.set('folderId', next.folder); else p.delete('folderId'); }
      return p;
    }, { replace });
  }, [setSearchParams]);
  const openFolder = useCallback((id, opts) => go({ folder: id }, opts), [go]);
  const pickClient = useCallback(id => go({ client: id, folder: 'all' }), [go]);
  const toProjects = useCallback(() => go({ client: null, folder: null }), [go]);

  const [counts, setCounts]     = useState({ unfiledCount: 0, totalCount: 0 });
  const [tree, setTree]         = useState([]);   // this client's folders: the Move dialog's choices
  const [files, setFiles]       = useState([]);
  const [filesState, setFilesState] = useState('loading');
  const [filesError, setFilesError] = useState(null);
  const [retention, setRetention] = useState(null);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [linked, setLinked]     = useState(null);   // the ?fileId= file, shown first
  const [summary, setSummary]   = useState(null);
  const [summaryError, setSummaryError] = useState(false);
  const [browse, setBrowse]     = useState(null);   // { folder, children, assets }
  const [downloading, setDownloading] = useState(null);
  const [deletingId, setDeletingId]   = useState(null);
  const [folderName, setFolderName]   = useState(null);   // the New folder dialog's field; null = closed
  const [editing, setEditing]         = useState(null);   // { action: 'rename' | 'move' | 'notes', file, value }
  const [saving, setSaving]           = useState(false);
  const [preflight, setPreflight]     = useState(null);
  const [dropOver, setDropOver]       = useState(false);
  const fileInput = useRef(null);
  const folderInput = useRef(null);

  // ── Loading: the Console page's effects, in its order, so a visit makes the same requests ──────────────────────

  useEffect(() => {
    const fileId = searchParams.get('fileId');
    if (!fileId) return;
    api.get(`${BASE}/files/${fileId}`)
      .then(({ data }) => {
        setLinked(data);
        go({ client: data.clientId, folder: data.folderId || 'unfiled' }, { replace: true });
      })
      .catch(() => {
        toast('That file is no longer available.', { tone: 'warn' });
        go({}, { replace: true });
      });
  }, [searchParams, go, toast]);

  const loadSummary = useCallback(() => {
    setSummaryError(false);
    api.get(`${BASE}/summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummaryError(true));
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // A client account joined to one client lands in it.
  useEffect(() => {
    if (!summary || clientId || searchParams.get('fileId')) return;
    if ((summary.clients || []).length === 1) go({ client: summary.clients[0].clientId, folder: 'all' }, { replace: true });
  }, [summary, clientId, searchParams, go]);

  const atRoot = selected === 'all' || selected === 'unfiled';

  const loadBrowse = useCallback(async () => {
    if (!clientId) { setBrowse(null); return; }
    const root = selected === 'all' || selected === 'unfiled';
    try {
      const { data } = await api.get(`${BASE}/folders/${root ? 'root' : selected}/children`, root ? { params: { clientId } } : undefined);
      setBrowse(data);
    } catch {
      setBrowse(null);
    }
  }, [clientId, selected]);
  useEffect(() => { loadBrowse(); }, [loadBrowse]);

  // The folder tree call: the Unfiled and All files counts, and the folders a file can move to.
  const loadTree = useCallback(async () => {
    if (!clientId) { setTree([]); setCounts({ unfiledCount: 0, totalCount: 0 }); return; }
    try {
      const { data } = await api.get(`${BASE}/folders`, { params: { clientId } });
      setTree(data.folders || []);
      setCounts({ unfiledCount: data.unfiledCount || 0, totalCount: data.totalCount || 0 });
    } catch (err) { toast(errMsg(err, 'The folders did not load. Refresh the page to try again.'), { tone: 'danger' }); }
  }, [clientId, toast]);

  const loadFiles = useCallback(async () => {
    if (!clientId) { setFiles([]); setTotal(0); return; }
    setFilesState('loading');
    try {
      const params = { clientId, page, pageSize: PAGE_SIZE };
      if (selected === 'all')          params.folderId = 'all';
      else if (selected !== 'unfiled') params.folderId = selected;
      if (search.trim())               params.search = search.trim();
      const { data } = await api.get(`${BASE}/files`, { params });
      setFiles(data.rows || []);
      setTotal(data.total || 0);
      if (data.retention) setRetention(data.retention);
      setFilesState('data');
    } catch (err) {
      setFilesError(errMsg(err, 'Check your connection, then try again.'));
      setFiles([]);
      setFilesState('error');
    }
  }, [clientId, selected, search, page]);

  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => {
    const t = setTimeout(loadFiles, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [loadFiles, search]);
  useEffect(() => { setPage(1); }, [selected, search, clientId]);

  const refresh = useCallback(() => { loadTree(); loadFiles(); loadBrowse(); }, [loadTree, loadFiles, loadBrowse]);
  useUploadDone(item => { if (item.meta?.kind === 'storage') refresh(); });

  // ── Where you are ───────────────────────────────────────────────────────────────────────────────────────────────

  const clients = disambiguate(summary?.clients || []);
  const clientName = clean(browse?.folder?.clientName)
    || clients.find(c => c.clientId === clientId)?.displayName || 'Your files';
  const crumbs = browse?.folder?.breadcrumb || [];
  const currentFolder = atRoot ? null : crumbs[crumbs.length - 1];
  const here = selected === 'unfiled' ? 'Unfiled' : clean(currentFolder?.name) || clientName;
  const goUp = () => {
    if (crumbs.length > 1) openFolder(crumbs[crumbs.length - 2].id);
    else if (!atRoot || selected === 'unfiled') openFolder('all');
    else toProjects();
  };
  const canGoUp = selected !== 'all' || clients.length > 1;

  // ── Uploads (as on the Console page: files into a folder or Unfiled; a folder at the root of where you are) ────

  const canUploadFiles  = canWrite && !!clientId && selected !== 'all';
  const canUploadFolder = canWrite && !!clientId;
  const destFolderId = atRoot ? null : selected;
  const fileTarget   = selected === 'unfiled' ? 'Unfiled' : clean(currentFolder?.name) || 'this folder';
  const folderTarget = selected === 'all' ? `the top of ${clientName}` : fileTarget;

  const queueFile = useCallback((file, dest) => {
    enqueueUpload({
      file,
      endpoints: UPLOAD_ENDPOINTS,
      startBody: { clientId, folderId: dest || null },
      idFieldName: 'archiveId',
      confirm: async archiveId => (await api.post(`${BASE}/upload/confirm`, { archiveId })).data,
      meta: { kind: 'storage', clientId, folderId: dest || null },
    });
  }, [clientId]);

  function handleFiles(list) {
    const all = [...(list || [])];
    const accepted = all.filter(f => ACCEPT_RE.test(f.name));
    if (!accepted.length) {
      if (all.length) toast('None of those file types can be uploaded here.', { tone: 'warn' });
      return;
    }
    accepted.forEach(f => queueFile(f, destFolderId));
    toast(`${plural(accepted.length, 'file')} uploading to ${fileTarget}. Progress is in the upload sheet.`, { tone: 'info' });
  }

  async function walkAndPreflight(source) {
    const dest = destFolderId;
    setPreflight({ walking: true, count: 0, dest });
    try {
      const entries = source.dataTransfer
        ? await entriesFromDataTransfer(source.dataTransfer, c => setPreflight(p => (p?.walking ? { ...p, count: c } : p)))
        : await entriesFromPicker(source.fileList, c => setPreflight(p => (p?.walking ? { ...p, count: c } : p)));
      if (!entries) {
        setPreflight(null);
        toast('This browser cannot read dropped folders. Use Upload folder instead.', { tone: 'warn' });
        return;
      }
      const summaryNext = buildPreflight(entries);
      if (!summaryNext.fileCount) {
        setPreflight(null);
        toast('That folder contains no uploadable files.', { tone: 'warn' });
        return;
      }
      try {
        const { data } = await api.post(`${BASE}/folders/resolve`, { clientId, parentId: dest || null, paths: summaryNext.folderPaths, dryRun: true });
        setPreflight({ summary: summaryNext, dry: data, error: null, dest });
      } catch (err) {
        setPreflight({ summary: summaryNext, dry: null, error: errMsg(err, 'That folder could not be checked.'), dest });
      }
    } catch (err) {
      setPreflight(null);
      toast(errMsg(err, 'That folder could not be read.'), { tone: 'danger' });
    }
  }

  async function confirmFolderUpload() {
    const { summary: s, dest } = preflight;
    setPreflight(null);
    try {
      const { data } = await api.post(`${BASE}/folders/resolve`, { clientId, parentId: dest || null, paths: s.folderPaths });
      const byPath = new Map(data.resolved.map(r => [r.originalPath.join('/'), r.folderId]));
      s.entries.forEach(e => queueFile(e.file, e.segments.length ? (byPath.get(e.segments.join('/')) || dest || null) : (dest || null)));
      refresh();
      toast(`${plural(s.fileCount, 'file')} uploading. Progress is in the upload sheet.`, { tone: 'info' });
    } catch (err) {
      toast(errMsg(err, 'The folder structure was not created. Try again.'), { tone: 'danger' });
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDropOver(false);
    if (dropContainsDirectory(e.dataTransfer)) { if (canUploadFolder) walkAndPreflight({ dataTransfer: e.dataTransfer }); }
    else if (canUploadFiles) handleFiles(e.dataTransfer.files);
    else if (canWrite) toast('Open a folder, or Unfiled, to upload files into it.', { tone: 'warn' });
  }
  const dropProps = canUploadFolder ? {
    onDragOver: e => { if ([...(e.dataTransfer?.types || [])].includes('Files')) { e.preventDefault(); setDropOver(true); } },
    onDragLeave: e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropOver(false); },
    onDrop,
  } : {};

  async function createFolder() {
    const name = (folderName || '').trim();
    if (!name) return;
    try {
      await api.post(`${BASE}/folders`, { clientId, parentId: atRoot ? null : selected, name });
      setFolderName(null);
      loadTree();
      loadBrowse();
      toast(`Folder ${name} created.`);
    } catch (err) { toast(errMsg(err, 'The folder was not created. Try again.'), { tone: 'danger' }); }
  }

  async function deleteFile(file) {
    const ok = await confirm({
      title: `Delete ${clean(file.fileName)} from Storage?`,
      body: deleteBody(retention, false),
      confirmLabel: 'Delete file',
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(file.id);
    try {
      await api.delete(`${BASE}/files/${file.id}`);
      if (linked?.id === file.id) setLinked(null);
      refresh();
      toast(`${clean(file.fileName)} deleted.`);
    } catch (err) { toast(errMsg(err, 'The file was not deleted. Try again.'), { tone: 'danger' }); }
    finally { setDeletingId(null); }
  }

  // Rename, Move, Notes on an own upload: the Console file panel's PATCH, one field per action, same body.
  const EDIT = {
    rename: { body: v => ({ fileName: v.trim() }), fallback: 'The file was not renamed. Try again.', done: 'File renamed.' },
    move:   { body: v => ({ folderId: v || null }), fallback: 'The file was not moved. Try again.', done: 'File moved.' },
    notes:  { body: v => ({ notes: v }), fallback: 'The notes did not save. Try again.', done: 'Notes saved.' },
  };
  const startEdit = (action, file) => setEditing({
    action, file, value: action === 'rename' ? file.fileName || '' : action === 'move' ? file.folderId || '' : file.notes || '',
  });
  const editBefore = !editing ? '' : editing.action === 'rename' ? editing.file.fileName || ''
    : editing.action === 'move' ? editing.file.folderId || '' : editing.file.notes || '';
  const editReady = !!editing && editing.value !== editBefore && (editing.action !== 'rename' || (!!editing.value.trim() && editing.value.trim() !== editBefore));

  async function saveEdit() {
    if (!editReady || saving) return;
    const { action, file, value } = editing;
    setSaving(true);
    try {
      const { data } = await api.patch(`${BASE}/files/${file.id}`, EDIT[action].body(value));
      if (linked?.id === file.id) setLinked(data);
      setEditing(null);
      refresh();
      toast(EDIT[action].done);
    } catch (err) { toast(errMsg(err, EDIT[action].fallback), { tone: 'danger' }); }
    finally { setSaving(false); }
  }

  // ── Header ──────────────────────────────────────────────────────────────────────────────────────────────────────

  usePageHeader({
    title: 'Storage',
    meta: clientId ? clientName : undefined,
    actions: (
      <>
        {canWrite && clientId && (
          <>
            <Button variant="quiet" portal icon={FolderPlus} iconOnly={phone} label="New folder" onClick={() => setFolderName('')}>New folder</Button>
            <Button variant="secondary" portal icon={FolderUp} disabled={!canUploadFolder} onClick={() => folderInput.current?.click()}>Upload folder</Button>
            <Button variant="primary" portal icon={Upload} disabled={!canUploadFiles} onClick={() => fileInput.current?.click()}
              title={selected === 'all' ? 'Open a folder, or Unfiled, to upload files into it' : undefined}>Upload files</Button>
          </>
        )}
        {tier && <PortalTierToggle tier={tier.tier} toggle={tier.toggle} />}
      </>
    ),
  });

  // ── Rows ────────────────────────────────────────────────────────────────────────────────────────────────────────

  const spansFolders = selected === 'all' || !!search.trim();
  const folders = selected === 'unfiled' || search.trim() ? [] : (browse?.children || []);
  const showUnfiled = selected === 'all' && !search.trim() && (canWrite || counts.unfiledCount > 0);
  const nothingStored = selected === 'all' && !search.trim() && filesState === 'data' && total === 0 && folders.length === 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fileRow = (f, { current = false, folderLine = spansFolders } = {}) => {
    const name = clean(f.fileName);
    const ready = f.uploadStatus === 'complete' || !f.uploadStatus;
    const mine = mayDeleteFile(deletePerm, f);
    const by = mine ? 'Uploaded by you' : clean(f.uploadedByName) ? `Uploaded by ${clean(f.uploadedByName)}` : '';
    return (
      <ListRow key={f.id} portal current={current} data-key={f.id}
        leading={<Icon icon={kindIcon(f.fileName)} size={20} />}
        title={name}
        meta={(
          <span className="ui-cluster">
            <span className="ui-tc">{formatBytes(f.fileSizeBytes)}</span>
            <span>{fmtDate(f.createdAt)}</span>
            {folderLine && <span>{clean(f.folderName) || 'Unfiled'}</span>}
            {by && <span>{by}</span>}
          </span>
        )}
        trailing={(
          <>
            {f.uploadStatus === 'pending' && <Badge tone="info">Uploading</Badge>}
            {f.uploadStatus === 'failed' && <Badge tone="danger">Upload failed</Badge>}
            {ready && (
              <Button variant="secondary" portal icon={Download} iconOnly={phone} label={`Download ${name}`} aria-label={`Download ${name}`}
                data-testid="storage-download" onClick={() => setDownloading(f)}>Download</Button>
            )}
            {mine && (
              <FileMenu name={name} busy={deletingId === f.id} items={[
                { label: 'Rename', icon: Pencil, testId: 'storage-file-rename', onSelect: () => startEdit('rename', f) },
                { label: 'Move', icon: FolderInput, testId: 'storage-file-move', onSelect: () => startEdit('move', f) },
                { label: 'Notes', icon: StickyNote, testId: 'storage-file-notes', onSelect: () => startEdit('notes', f) },
                { separator: true },
                { label: 'Delete', icon: Trash2, danger: true, testId: 'storage-file-delete', onSelect: () => deleteFile(f) },
              ]} />
            )}
          </>
        )} />
    );
  };

  const emptyFiles = search.trim()
    ? { message: 'No file name matches that search. Try a different word, or clear it.', action: { label: 'Clear search', icon: X, onClick: () => setSearch('') } }
    : selected === 'all'
      ? { message: canWrite ? 'Nothing is stored here yet. Open Unfiled, or create a folder, and upload files into it.' : 'Nothing is stored here yet.',
          ...(canWrite ? { action: { label: 'New folder', icon: FolderPlus, onClick: () => setFolderName('') } } : {}) }
      : selected === 'unfiled'
        ? { message: canWrite ? 'Nothing is unfiled. Files uploaded here, outside any folder, show in this list.' : 'Nothing is unfiled.' }
        : { message: canWrite ? 'This folder is empty. Upload files to keep them here.' : 'This folder is empty.' };

  // ── Render ──────────────────────────────────────────────────────────────────────────────────────────────────────

  return (
    <div className="ui ui-page ui-page--portal ui-droptarget" data-drop={dropOver || undefined} {...dropProps}>
      <input ref={fileInput} type="file" multiple hidden accept={ACCEPT_EXTS}
        onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
      <input ref={folderInput} type="file" multiple hidden webkitdirectory="" directory=""
        onChange={e => { if (e.target.files?.length) walkAndPreflight({ fileList: e.target.files }); e.target.value = ''; }} />

      {!clientId ? (
        <section className="ui-section" aria-label="Projects">
          {summaryError ? (
            <ErrorState title="Your storage did not load" message="Check your connection, then try again." onRetry={loadSummary} />
          ) : !summary || searchParams.get('fileId') || clients.length === 1 ? (
            <div className="ui-list" aria-busy="true">{[0, 1, 2].map(i => <Skeleton key={i} variant="block" height="76px" />)}</div>
          ) : clients.length === 0 ? (
            <EmptyState message="No project is linked to your account yet. Ask the team you work with to add you to one." />
          ) : (
            <div className="ui-list ui-region">
              {clients.map(c => (
                <ListRow key={c.clientId} portal data-key={c.clientId} onClick={() => pickClient(c.clientId)}
                  leading={<Icon icon={Folder} size={20} />} title={c.displayName}
                  meta={<span className="ui-cluster"><span>{plural(c.fileCount, 'file')}</span><span className="ui-tc">{formatBytes(c.totalBytes)}</span></span>}
                  trailing={<Icon icon={ChevronRight} size={20} />} />
              ))}
            </div>
          )}
        </section>
      ) : (
        <>
          {linked && (
            <section className="ui-section" aria-label="The file you opened">
              <div className="ui-section__head ui-section__head--center">
                <h2 className="ui-section__title">The file you opened</h2>
                <span className="ui-section__actions">
                  <Button variant="quiet" portal iconOnly icon={X} label="Hide this file" onClick={() => { setLinked(null); dismissFinishedDownloads(); }} />
                </span>
              </div>
              <div className="ui-list ui-region">{fileRow(linked, { current: true, folderLine: true })}</div>
            </section>
          )}

          <div className="ui-toolbar">
            <nav className="ui-cluster" aria-label="Folder path">
              {canGoUp && <Button variant="secondary" portal iconOnly icon={ArrowUp} label={crumbs.length || selected === 'unfiled' ? 'Up one level' : 'Back to your projects'} onClick={goUp} />}
              {/* On a phone the path is the folder you are in; Up walks back. */}
              {selected === 'all' || phone
                ? <span className="ui-heading" aria-current="page">{phone ? here : clientName}</span>
                : <Button variant="quiet" portal onClick={() => openFolder('all')}>{clientName}</Button>}
              {selected === 'unfiled' && !phone && <><Icon icon={ChevronRight} size={20} /><span className="ui-heading" aria-current="page">Unfiled</span></>}
              {!phone && crumbs.map((c, i) => (
                <span key={c.id} className="ui-cluster">
                  <Icon icon={ChevronRight} size={20} />
                  {i === crumbs.length - 1
                    ? <span className="ui-heading" aria-current="page">{clean(c.name)}</span>
                    : <Button variant="quiet" portal onClick={() => openFolder(c.id)}>{clean(c.name)}</Button>}
                </span>
              ))}
            </nav>
            <span className="ui-spacer" />
            {!nothingStored && <Input className="ui-search" aria-label={`Search file names in ${here}`} icon={Search} type="search" placeholder="Search file names"
              value={search} onChange={e => setSearch(e.target.value)} />}
          </div>

          {(folders.length > 0 || showUnfiled) && (
            <section className="ui-section" aria-label="Folders">
              <h2 className="ui-section__title">Folders</h2>
              <div className="ui-list ui-region">
                {folders.map(f => (
                  <ListRow key={f.id} portal data-key={f.id} onClick={() => openFolder(f.id)}
                    leading={<Icon icon={Folder} size={20} />} title={clean(f.name)}
                    meta={(
                      <span className="ui-cluster">
                        <span>{plural(f.fileCount, 'file')}{f.folderCount > 0 ? `, ${plural(f.folderCount, 'folder')}` : ''}</span>
                        <span className="ui-tc">{formatBytes(f.totalBytes)}</span>
                      </span>
                    )}
                    trailing={<Icon icon={ChevronRight} size={20} />} />
                ))}
                {showUnfiled && (
                  <ListRow portal onClick={() => openFolder('unfiled')} leading={<Icon icon={Folder} size={20} />} title="Unfiled"
                    meta={counts.unfiledCount > 0 ? `${plural(counts.unfiledCount, 'file')} outside any folder` : 'Files kept outside any folder'} trailing={<Icon icon={ChevronRight} size={20} />} />
                )}
              </div>
            </section>
          )}

          <section className="ui-section" aria-label="Files">
            <div className="ui-section__head">
              <h2 className="ui-section__title">{selected === 'all' && !search.trim() ? 'All files' : 'Files'}</h2>
              {filesState === 'data' && total > 0 && <span className="ui-section__note">{plural(total, 'file')}</span>}
            </div>
            {canWrite && selected === 'all' && !search.trim() && filesState === 'data' && (total > 0 || folders.length > 0) && (
              <p className="ui-section__note">To upload files, open a folder or Unfiled first.</p>
            )}
            {filesState === 'error' ? (
              <ErrorState title="The files did not load" message={filesError} onRetry={loadFiles} />
            ) : filesState === 'loading' ? (
              <div className="ui-list ui-region" aria-busy="true">{[0, 1, 2, 3].map(i => <Skeleton key={i} variant="block" height="76px" />)}</div>
            ) : files.length === 0 ? (
              <EmptyState {...emptyFiles} />
            ) : (
              <div className="ui-list ui-region">{files.map(f => fileRow(f))}</div>
            )}
            {totalPages > 1 && (
              <div className="ui-cluster">
                <Button variant="secondary" portal disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
                <span className="ui-meta">Page {page} of {totalPages}</span>
                <Button variant="secondary" portal disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</Button>
              </div>
            )}
          </section>
        </>
      )}

      <StorageDownloadDock />

      {downloading && <StorageDownloadModal file={downloading} api={api} base={BASE} onClose={() => setDownloading(null)} />}

      <Modal open={folderName !== null} onClose={() => setFolderName(null)} title="New folder"
        footer={(
          <>
            <Button variant="quiet" portal onClick={() => setFolderName(null)}>Cancel</Button>
            <Button variant="primary" portal disabled={!(folderName || '').trim()} onClick={createFolder}>Create folder</Button>
          </>
        )}>
        {folderName !== null && (
          <form className="ui-stack" onSubmit={e => { e.preventDefault(); createFolder(); }}>
            <Input label="Folder name" value={folderName} data-autofocus autoComplete="off"
              hint={`Created in ${atRoot ? `the top of ${clientName}` : fileTarget}.`}
              onChange={e => setFolderName(e.target.value)} />
          </form>
        )}
      </Modal>

      {editing && (
        <Modal onClose={() => !saving && setEditing(null)}
          title={editing.action === 'rename' ? `Rename ${clean(editing.file.fileName)}` : editing.action === 'move' ? `Move ${clean(editing.file.fileName)}` : `Notes on ${clean(editing.file.fileName)}`}
          footer={(
            <>
              <Button variant="quiet" portal disabled={saving} onClick={() => setEditing(null)}>Cancel</Button>
              <Button variant="primary" portal data-testid="storage-file-save" loading={saving} disabled={!editReady} onClick={saveEdit}>
                {editing.action === 'rename' ? 'Save name' : editing.action === 'move' ? 'Move file' : 'Save notes'}
              </Button>
            </>
          )}>
          <form className="ui-stack" onSubmit={e => { e.preventDefault(); saveEdit(); }}>
            {editing.action === 'rename' && (
              <Input label="File name" value={editing.value} data-autofocus autoComplete="off" spellCheck={false}
                hint="Changes the name shown here. The stored file stays as it is."
                onChange={e => setEditing(x => ({ ...x, value: e.target.value }))} />
            )}
            {editing.action === 'move' && (
              <Select label="Folder" value={editing.value} data-autofocus
                hint={`Now in ${clean(editing.file.folderName) || 'Unfiled'}.`}
                onChange={e => setEditing(x => ({ ...x, value: e.target.value }))}
                options={[{ value: '', label: 'Unfiled' }, ...indentOptions(flattenTree(tree))]} />
            )}
            {editing.action === 'notes' && (
              <Textarea label="Notes" rows={4} value={editing.value} data-autofocus
                hint="The team working on this project sees these notes too."
                onChange={e => setEditing(x => ({ ...x, value: e.target.value }))} />
            )}
          </form>
        </Modal>
      )}

      {preflight && (
        <PreflightModal preflight={preflight} destination={preflight.dest ? fileTarget : folderTarget} note={PREFLIGHT_NOTE}
          onCancel={() => setPreflight(null)} onConfirm={confirmFolderUpload} />
      )}
    </div>
  );
}

// The row menu on a file the client uploaded (as the Console's folder menu: a Popover holding a Menu).
function FileMenu({ name, busy, items }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} align="end" label={`File actions for ${name}`}
      trigger={({ toggle, ref, props }) => (
        <Button ref={ref} variant="secondary" portal iconOnly icon={MoreHorizontal} label={`File actions for ${name}`} loading={busy}
          data-testid="storage-file-menu" onClick={toggle} {...props} />
      )}>
      <Menu label={`File actions for ${name}`} onClose={() => setOpen(false)} items={items} />
    </Popover>
  );
}
